import { AtualizarCotacaoFornecedorParcelaRequest } from '@shared/services/requests/compras/atualizar-cotacao-fornecedor-parcela.request';
import { dataPorDias, inicioDoDia, parseParcelasDias } from '@shared/helpers/condicao-pagamento.util';
import { AcaoAlteracaoProdutoEnum } from '@shared/enums/compras/acao-alteracao-produto.enum';
import { CriterioCotacaoEnum } from '@shared/enums/compras/criterio-cotacao.enum';
import { OrigemCotacaoEnum } from '@shared/enums/compras/origem-cotacao.enum';

export { dataPorDias, inicioDoDia, parseParcelasDias };
import { DetalheCompraResponse } from '@shared/services/responses/compras/detalhe-compra.response';
import { CampoEtapaValorItemStruct } from '@shared/services/structs/compras/campo-etapa-valor-item.struct';
import { CotacaoFornecedorItemStruct } from '@shared/services/structs/compras/cotacao-fornecedor-item.struct';
import { CotacaoFornecedorStruct } from '@shared/services/structs/compras/cotacao-fornecedor.struct';
import { TimelineHistoricoItem } from '@shared/components/timeline-historico/timeline-historico.component';
import { OrigemOcorrenciaEnum } from '@shared/enums/compras/origem-ocorrencia.enum';
import { CompraOcorrenciaStruct } from '@shared/services/structs/compras/compra-ocorrencia.struct';
// import type: evita ciclo de módulo em runtime (processo-compras importa o dialog).
import type { KanbanCard, KanbanStage, Person } from '../../processo-compras.models';

export type CardDetalhe = DetalheCompraResponse;

export interface CardEditDialogData {
  card: KanbanCard;
  allStages: KanbanStage[];
  transitions: Record<string, string[]>;
  detalhe?: CardDetalhe;
  usersById?: Map<number, Person>;
  // ids dos campos nativos obrigatórios da etapa, pré-carregados pela página
  // para o "*" já aparecer correto na abertura.
  nativosObrigatorios?: number[];
  /**
   * NF do "modo NF" (a nota, os pedidos dela e os itens) quando quem abre já sabe qual é: a moldura do quadro
   * ou a troca de pedido/NF dentro do card. Sem ela, o card resolve sozinho pela NF mais recente do pedido.
   */
  nota?: CardNotaContexto;
  /** Permissão de cancelar do usuário (a mesma do arraste para Cancelado no quadro). */
  podeCancelarCompra?: boolean;
}

/** Pedido da NF no modo NF do card. Etapa (cor/ícone) e itens da NF chegam com o contexto da nota. */
export interface CardNotaPedido {
  idCompra: number;
  /** Código já formatado ("#24"). */
  codigo: string;
  nomeEtapa: string;
  /** Valor como no card ("R$ 1.234,00" ou "—"). */
  valorLabel: string;
  nomeFornecedor: string;
  corEtapa?: string | null;
  iconeEtapa?: string | null;
  qtdItensNota?: number;
}

/** Nota da moldura do quadro: os pedidos chegam na ordem do quadro, com o que o card já resolveu. */
export interface CardNotaContexto {
  idNFeEntrada: number;
  /** Rótulo da moldura ("NF-e 42342/3"). */
  rotulo: string;
  pedidos: CardNotaPedido[];
}

export interface AuditChange {
  campo: string;
  label: string;
  acao: 'alterado' | 'adicionado' | 'removido';
  item?: string | null;
  de?: string | null;
  para?: string | null;
}

/**
 * Item do histórico da compra: o item da linha do tempo compartilhada mais o que só a coluna de
 * detalhe usa (diff da edição, aviso de aprovação e compras citadas), lidos de `dados`.
 */
export interface CardHistoryItem extends TimelineHistoricoItem {
  alteracoes?: AuditChange[];
  notificacao?: HistoryNotification;
  compras?: HistoryCompraLink[];
}

/** Detalhe do aviso de aprovação: por qual faixa saiu e quem recebeu. */
export interface HistoryNotification {
  faixa: string;
  nomes: string[];
}

/**
 * Compra citada pela ocorrência (cotação de origem × pedidos gerados). Vem com id porque a
 * cotação arquivada não está no quadro: o card dela só é alcançado por aqui.
 */
export interface HistoryCompraLink {
  idCompra: number;
  codigo: string;
}

export const AVATAR_COLORS = ['#B73232', '#1E5DB8', '#1E8E3E', '#7A4FBF', '#C26A00', '#0F7A8B'];

/** Cor determinística de avatar a partir de uma seed (iniciais/nome/id). */
export function avatarColorFromSeed(seed: string): string {
  if (!seed) return AVATAR_COLORS[0];
  const hash = Array.from(seed).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/** Formata bytes em texto legível ("12 KB", "1,3 MB"). */
export function formatBytes(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Deduz o tipo do anexo pela extensão do arquivo. */
export function kindFromName(name: string): 'pdf' | 'xlsx' | 'doc' | 'img' {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (ext === 'pdf') return 'pdf';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'xlsx';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'img';
  return 'doc';
}

/** Converte ids de usuário em nomes no diff de responsável (auditoria). */
function formatAuditChange(change: AuditChange, usersById?: Map<number, Person>): AuditChange {
  if (change.campo !== 'idUsuarioResponsavel') return change;

  const userName = (value?: string | null): string | null => {
    if (!value || value === '(vazio)') return value ?? null;
    const id = Number(value);
    if (!Number.isFinite(id)) return value;
    return usersById?.get(id)?.name || value;
  };

  return {
    ...change,
    de: userName(change.de),
    para: userName(change.para),
  };
}

/**
 * Monta o histórico a partir das ocorrências da compra (os comentários já vêm projetados como Anotação).
 * Sem nome no registro, o autor é resolvido pelo cadastro de usuários e, sem usuário, é a própria origem.
 * A etapa anterior/atual só vem preenchida na Criação (só a atual), na Mudança de status e no Cancelamento:
 * os badges aparecem exatamente nessas ocorrências.
 */
export function montarHistorico(ocorrencias: CompraOcorrenciaStruct[], usersById?: Map<number, Person>): CardHistoryItem[] {
  return ocorrencias.map(o => {
    let alteracoes: AuditChange[] | undefined;
    let notificacao: HistoryNotification | undefined;
    let compras: HistoryCompraLink[] | undefined;
    if (o.dados) {
      try {
        const parsed = JSON.parse(o.dados);
        if (Array.isArray(parsed?.alteracoes) && parsed.alteracoes.length) {
          alteracoes = (parsed.alteracoes as AuditChange[]).map(change => formatAuditChange(change, usersById));
        } else if (Array.isArray(parsed?.idsUsuarioNotificado)) {
          notificacao = {
            faixa: parsed.faixa || '',
            nomes: (parsed.idsUsuarioNotificado as number[])
              .map(id => usersById?.get(id)?.name || `Usuário ${id}`),
          };
        } else if (Array.isArray(parsed?.compras) && parsed.compras.length) {
          compras = (parsed.compras as HistoryCompraLink[])
            .filter(c => c && c.idCompra)
            .map(c => ({ idCompra: c.idCompra, codigo: String(c.codigo ?? '') }));
        }
      } catch { /* dados malformado: fica só a descrição */ }
    }
    return {
      icone: o.nomeIconeTipoOcorrencia,
      titulo: o.nomeTipoOcorrencia,
      descricao: o.descricao,
      detalhe: o.detalhes,
      justificativa: o.justificativa,
      // "Detalhes" só aparece quando a coluna de detalhe tem o que mostrar.
      dados: alteracoes || notificacao || compras ? o.dados : null,
      quando: o.dataOcorrencia,
      quemNome: o.nomeUsuario || usersById?.get(o.idUsuario ?? -1)?.name || o.nomeOrigemOcorrencia,
      nomeOrigem: o.nomeOrigemOcorrencia,
      iconeOrigem: o.nomeIconeOrigemOcorrencia,
      exibirOrigem: o.idOrigemOcorrencia !== OrigemOcorrenciaEnum.Usuario,
      nomeStatusAnterior: o.nomeEtapaKanbanAnterior,
      nomeStatusAtual: o.nomeEtapaKanbanAtual,
      alteracoes,
      notificacao,
      compras,
    };
  });
}

export interface CardComment {
  who: string;
  whoName?: string;
  whoColor?: string;
  when: string;
  text: string;
}

export const STAGE_META: Record<string, { stepNote?: string; stageLabel?: string; instruction: string }> = {
  solicitacao: {
    stepNote: 'ativa',
    instruction: 'Confirme produto e quantidade. Adicione fornecedores a cotar. Informe origem, prioridade e prazo para liberar o avanço.',
  },
  cotacao: {
    stepNote: 'cotações em andamento',
    instruction: 'Convide fornecedores, registre preços, prazos e tributos. Defina o critério de comparação e selecione o vencedor.',
  },
  // É o total fechado aqui que a alçada avalia na etapa seguinte.
  pedido: {
    stepNote: 'pedido em montagem',
    instruction: 'Feche o pedido: preços, tributos, descontos, frete, condições e parcelas. É este total que será submetido à aprovação na etapa seguinte.',
  },
  aprovacao: {
    stepNote: 'aguardando aprovação',
    instruction: 'Confira o pedido já fechado — itens, frete e total. Aprove ou rejeite; o envio ao fornecedor acontece aqui, somente depois da aprovação.',
  },
  faturado: {
    stepNote: 'NF vinculada',
    instruction: 'Confira a NF vinculada ao pedido: itens associados e vinculados liberam o avanço para a Conferência.',
  },
  conferencia: {
    instruction: 'Receba os volumes e confira os itens contra a NF vinculada. As duas ações são independentes e detectam divergências ao concluir.',
  },
  finalizado: {
    stageLabel: 'Concluído',
    instruction: 'Compra concluída. Estoque e financeiro são lançados pelas automações configuradas, pela NF ou pelo pedido. Confira os lançamentos abaixo e lance o que faltar.',
  },
  cancelado: {
    stageLabel: 'Cancelado',
    instruction: 'Este card foi cancelado.',
  },
  divergencia: {
    stageLabel: 'Divergência identificada',
    // Sem listar desfechos: os disponíveis dependem da origem da divergência e das movimentações configuradas (painel).
    instruction: 'Resolva a divergência pelo vínculo da nota ou pela conferência e escolha o desfecho no painel abaixo.',
  },
};

export interface CardAttachment {
  idAnexo?: number;
  name: string;
  size: string;
  kind: 'pdf' | 'xlsx' | 'doc' | 'img';
  who: string;
  whoName?: string;
  whoColor?: string;
  when: string;
  url?: string;
}

export const KIND_META: Record<string, { icon: string; color: string; bg: string }> = {
  pdf:  { icon: 'description',  color: '#D93025', bg: '#FCE8E6' },
  xlsx: { icon: 'table_chart',  color: '#1E8E3E', bg: '#E6F4EA' },
  doc:  { icon: 'description',  color: '#1E5DB8', bg: '#E8F1FF' },
  img:  { icon: 'image',        color: '#7A4FBF', bg: '#F1EAFB' },
};

export interface CardProduct {
  idCompraItem?: number | null;
  idProduto?: number | null;
  sku: string;
  name: string;
  marca?: string | null;
  qty: number;
  unit: string;
  /** null = estoque desconhecido (item já existente na compra, sem retorno de saldo do backend). */
  stock: number | null;
  price: string;
  valorUnitario?: number | null;
  isAvulso?: boolean;
  observacao?: string | null;
}

export interface MoveForward {
  to: string;
  label: string;
  kind: 'next' | 'skip' | 'danger' | 'alt';
  icon?: string;
}

// 'cancelado' and 'divergencia' are side/special stages that sit outside the main flow sequence.
export const SIDE_STAGE_KEYS = new Set(['cancelado', 'divergencia']);

/** Etapas de onde o pedido sem NF-e vai direto para Finalizado, mesmo fora das movimentações configuradas. */
export const ETAPAS_FINALIZACAO_SEM_NF = new Set(['pedido', 'aprovacao', 'faturado']);

export interface ProductSearchResult {
  idProduto?: number;
  sku: string;
  name: string;
  marca?: string | null;
  unit: string;
  stock: number;
  price: number;
}

export interface CardDynamicField extends CampoEtapaValorItemStruct {
  valorData?: Date | null;
  valorMultiselect?: number[];
  valorNumero?: number | null;
}

// ===== COTAÇÃO =====
export interface CotacaoSupplier {
  id: string;
  idCotacaoFornecedor: number;
  idFornecedor: number;
  initials: string;
  color: string;
  name: string;
  status: 'recebida' | 'pendente';
  /** Cotação já enviada ao fornecedor (tem datetime_envio). Distingue "não enviada" de "aguardando resposta". */
  enviada: boolean;
  when: string;
  tag: string;
  origem?: OrigemCotacaoEnum | null;
  email?: string | null;
  documento?: string | null;
  /** Observações da proposta digitadas pelo fornecedor (portal/planilha). Só leitura. */
  observacoes?: string | null;
}

export interface CotacaoCatalogItem {
  id: number;
  initials: string;
  color: string;
  name: string;
  cnpj: string;
  city: string;
  tag: string;
  category: string;
}

export interface CotacaoSelectionItem {
  sku: string;
  name: string;
  qty: number;
  supplier: string;
  supplierColor: string;
  /** Presente quando a seleção vem da matriz de comparação (seleção manual) — ver MatrixSelectionItem. */
  idFornecedor?: number;
  subtotal: number;
  /** Preço unitário real ofertado, presente quando a seleção vem da matriz — ver MatrixSelectionItem. */
  valorUnitario?: number | null;
  // Identificadores para persistir a seleção na compra (preço/fornecedor vencedor por item).
  idCompraItem?: number | null;
  valorUnitarioSel?: number | null;
  idFornecedorSel?: number | null;
}

/** Rótulos de apresentação do "Critério da escolha"; o estado e as comparações usam o enum. */
export const COTACAO_CRITERIOS: { id: CriterioCotacaoEnum; descricao: string }[] = [
  { id: CriterioCotacaoEnum.MenorPrecoTotal, descricao: 'Menor preço total' },
  { id: CriterioCotacaoEnum.MenorPrazoEntrega, descricao: 'Menor prazo de entrega' },
  { id: CriterioCotacaoEnum.MenorFrete, descricao: 'Menor frete' },
  { id: CriterioCotacaoEnum.DefinidoManualmente, descricao: 'Definido manualmente pelo comprador' },
];

export interface CotacaoParcela {
  dias: number;
  data: Date | null;
  /** Forma de pagamento (cadastro de Finanças); null = não informada. */
  idFormaPagamento: number | null;
}

export interface CotacaoParcelasState {
  expr: string;
  parcelas: CotacaoParcela[];
}

// ===== COTAÇÃO — parcelas: helpers puros =====
// Compartilhados entre a etapa de Cotação e o painel da matriz. Recebem a data-base por
// parâmetro para os dois operarem sobre a mesma base e dias ↔ data não divergirem.
// inicioDoDia/dataPorDias/parseParcelasDias vêm de shared/helpers/condicao-pagamento.util.ts
// (reexportadas no topo deste arquivo) — só a montagem de CotacaoParcela (com a forma de
// pagamento, específica de cotação) fica aqui.

export function gerarParcelasDe(dataBase: Date, expr: string): CotacaoParcela[] {
  return parseParcelasDias(expr).map(dias => ({
    dias,
    data: dataPorDias(dataBase, dias),
    idFormaPagamento: null,
  }));
}

/** "yyyy-MM-dd" → Date local (sem o deslocamento de fuso que `new Date(iso)` introduz). */
export function dataDaApi(valor: string | null | undefined): Date | null {
  const m = (valor ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * Parcelas persistidas → estado editável, ordenadas por sequência.
 *
 * O vencimento vem do banco quando existe: recalculá-lo como `hoje + dias` fazia a data andar
 * para frente a cada reabertura do card, e o valor deslocado era regravado no save seguinte.
 */
export function mapParcelasCotacao(dataBase: Date, parcelas: CotacaoFornecedorStruct['parcelas']): CotacaoParcela[] {
  return (parcelas ?? [])
    .slice()
    .sort((a, b) => (a.sequencia ?? 0) - (b.sequencia ?? 0))
    .map(p => {
      const dias = Math.max(0, p.dias ?? 0);
      return {
        dias,
        data: dataDaApi(p.dataVencimento) ?? dataPorDias(dataBase, dias),
        idFormaPagamento: p.idFormaPagamento ?? null,
      };
    });
}

/** Estado editável → payload da API, renumerando a sequência pela ordem exibida. */
export function parcelasParaRequest(dataBase: Date, parcelas: CotacaoParcela[]): AtualizarCotacaoFornecedorParcelaRequest[] {
  return parcelas.map((p, index) => {
    const dias = Math.max(0, Math.trunc(Number(p.dias) || 0));
    // Preserva a data em edição (que pode ser a persistida); só deriva de `dias` quando não há.
    const vencimento = p.data instanceof Date && !Number.isNaN(p.data.getTime())
      ? p.data
      : dataPorDias(dataBase, dias);
    return {
      sequencia: index + 1,
      dias,
      dataVencimento: toApiDate(vencimento),
      valor: null,
      idFormaPagamento: p.idFormaPagamento ?? null,
    };
  });
}

/** Estado inicial de parcelas de uma cotação: usa as persistidas ou deriva da expressão. */
export function estadoParcelasDaCotacao(dataBase: Date, cotacao: CotacaoFornecedorStruct | undefined): CotacaoParcelasState {
  const expr = cotacao?.condicaoPagamento || '';
  return {
    expr,
    parcelas: cotacao?.parcelas?.length
      ? mapParcelasCotacao(dataBase, cotacao.parcelas)
      : (expr ? gerarParcelasDe(dataBase, expr) : []),
  };
}

// ===== COTAÇÃO — helpers puros compartilhados entre o shell (resumo da aprovação)
// e o componente da etapa de cotação (ced-stage-cotacao).

/** Formata número para string pt-BR ("1.234,56"); vazio/nulo → "0,00". */
export function numToBR(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Identificador de exibição do processo de compra: "#" + código real, sem zeros à esquerda. */
export function formatCodigoCompra(codigo: string | number | null | undefined): string {
  if (codigo == null || codigo === '') return '';
  return '#' + String(codigo).replace(/^0+(?=\d)/, '');
}

/** Rótulo da NF como na moldura do quadro e na gaveta ("NF-e 42342/3"): número sem zeros à esquerda; série vazia vira 1. */
export function rotuloNotaFiscal(numero: string | null | undefined, serie: string | null | undefined): string {
  if (!numero) return 'NF-e —';
  return `NF-e ${numero.replace(/^0+(?=\d)/, '')}/${serie || '1'}`;
}

/** Exibe valor monetário; zero ou ausente → "—" (preço ainda não preenchido). */
export function fmtMoneyDisplay(n: number | null | undefined): string {
  if (n == null || n === 0) return '—';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Valor exibido no card do kanban: usa valorTotal da API ou total local quando a API ainda não refletiu a seleção. */
export function formatKanbanCardValue(
  valorTotal: number | null | undefined,
  fallbackTotal?: number | null,
): string {
  const n = (valorTotal != null && valorTotal > 0)
    ? valorTotal
    : (fallbackTotal != null && fallbackTotal > 0)
      ? fallbackTotal
      : null;
  if (n == null) return '—';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Converte string pt-BR ("1.234,56") para número. */
export function parseBR(n: string | number | null | undefined): number {
  if (typeof n === 'number') return n;
  return parseFloat(String(n || '0').replace(/\./g, '').replace(',', '.')) || 0;
}

/** Formata Date como "yyyy-MM-dd" para a API. */
export function toApiDate(value: Date): string {
  const yyyy = value.getFullYear();
  const mm = String(value.getMonth() + 1).padStart(2, '0');
  const dd = String(value.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Iniciais (até 2 letras) a partir do nome. */
export function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    // Só tokens que começam com letra: nomes como "Fornecedor - alfa" têm o "-"
    // como palavra e gerariam iniciais "F-" em vez de "FA".
    .filter(part => /^\p{L}/u.test(part))
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('') || 'F';
}

/** Nome exibido enquanto o lookup de pessoas não retorna o nome real. */
export function nomeFornecedorFallback(idFornecedor: number): string {
  return `Fornecedor ${idFornecedor}`;
}

/** Texto de status temporal da cotação ("respondido em ...", "enviado em ...", "convidado"). */
export function cotacaoWhen(c: CotacaoFornecedorStruct, respondida: boolean): string {
  const dt = respondida ? c.datetimeResposta : c.datetimeEnvio;
  if (!dt) return 'convidado';
  const label = respondida ? 'respondido' : 'enviado';
  return `${label} em ${new Date(dt).toLocaleDateString('pt-BR')}`;
}

/** Converte a struct do backend na visão de fornecedor convidado usada pela UI. */
export function mapCotacaoFornecedor(c: CotacaoFornecedorStruct): CotacaoSupplier {
  const name = nomeFornecedorFallback(c.idFornecedor);
  const color = avatarColorFromSeed(String(c.idFornecedor));
  // Reenvio depois da resposta reabre a cotação no portal: volta a aguardar a nova resposta.
  const respondida = !!c.datetimeResposta
    && !(c.datetimeEnvio && new Date(c.datetimeEnvio) > new Date(c.datetimeResposta));

  return {
    id: String(c.idCotacaoFornecedor),
    idCotacaoFornecedor: c.idCotacaoFornecedor,
    idFornecedor: c.idFornecedor,
    initials: initialsFromName(name),
    color,
    name,
    status: respondida ? 'recebida' : 'pendente',
    enviada: !!c.datetimeEnvio,
    when: cotacaoWhen(c, respondida),
    tag: respondida ? 'Respondido' : 'Pendente',
    origem: c.idOrigemCotacao ?? null,
    observacoes: c.observacoes ?? null,
  };
}

/**
 * Regra única de elegibilidade de uma oferta para a escolha automática (etapa e matriz):
 * cotação respondida, com preço e com disponibilidade positiva. Disponibilidade não informada
 * (null) não bloqueia — como no backend, que só marca "não cotado" quando ela é <= 0.
 */
export function isOfertaElegivelCotacao(
  respondida: boolean,
  valorUnitario: number | null | undefined,
  disponibilidade: number | null | undefined,
): boolean {
  return respondida && (valorUnitario ?? 0) > 0 && (disponibilidade == null || disponibilidade > 0);
}

/** Ordena candidatos de um item conforme o critério escolhido (menor primeiro). Frete 0 é o melhor. */
export function compararCandidatosCotacao(
  a: { cotacao: CotacaoFornecedorStruct; item: CotacaoFornecedorItemStruct },
  b: { cotacao: CotacaoFornecedorStruct; item: CotacaoFornecedorItemStruct },
  criterio: CriterioCotacaoEnum,
): number {
  const preco = (x: typeof a) => x.item.valorUnitario || 0;
  const prazo = (x: typeof a) => x.item.prazoEntregaDias ?? Number.POSITIVE_INFINITY;
  const frete = (x: typeof a) => x.cotacao.valorFrete ?? Number.POSITIVE_INFINITY;

  switch (criterio) {
    case CriterioCotacaoEnum.MenorPrazoEntrega:
      return (prazo(a) - prazo(b)) || (preco(a) - preco(b));
    case CriterioCotacaoEnum.MenorFrete:
      return (frete(a) - frete(b)) || (preco(a) - preco(b));
    default: // Menor preço total
      return preco(a) - preco(b);
  }
}

/**
 * Calcula a seleção vencedora (um fornecedor por item) a partir das cotações respondidas.
 * Não cobre o critério "Definido manualmente pelo comprador" — nesse modo a seleção vem da matriz.
 */
export function calcularSelecaoCotacao(
  cotacoes: CotacaoFornecedorStruct[],
  products: CardProduct[],
  suppliers: CotacaoSupplier[],
  criterio: CriterioCotacaoEnum,
): CotacaoSelectionItem[] {
  const fornecedores = new Map(suppliers.map(s => [s.idCotacaoFornecedor, s]));
  const selecionados: CotacaoSelectionItem[] = [];

  for (const produto of products) {
    const idCompraItem = produto.idCompraItem;
    if (!idCompraItem) continue;

    // Candidatos: ofertas elegíveis deste item (isOfertaElegivelCotacao).
    const candidatos = cotacoes
      .map(c => {
        const item = (c.itens || []).find(i => i.idCompraItem === idCompraItem && !i.isNaoCotado
          && isOfertaElegivelCotacao(!!c.datetimeResposta, i.valorUnitario, i.disponibilidade));
        return item ? { cotacao: c, item } : null;
      })
      .filter((x): x is { cotacao: CotacaoFornecedorStruct; item: CotacaoFornecedorItemStruct } => x != null);

    if (!candidatos.length) continue;

    const melhor = candidatos.sort((a, b) => compararCandidatosCotacao(a, b, criterio)).shift()!;
    const valor = melhor.item.valorUnitario || 0;
    // Quantidade do pedido = "pedir" gravada na cotação do vencedor (cotacao_fornecedor_item.quantidade,
    // editável na matriz e semeada com a solicitada); a solicitada só cobre item ainda sem esse valor.
    // Não é a disponibilidade (item.disponibilidade), que só limita a elegibilidade.
    const quantidade = melhor.item.quantidade ?? produto.qty ?? 0;
    const supplier = fornecedores.get(melhor.cotacao.idCotacaoFornecedor);
    selecionados.push({
      sku: produto.sku || String(idCompraItem),
      name: produto.name,
      qty: quantidade,
      supplier: supplier?.name || nomeFornecedorFallback(melhor.cotacao.idFornecedor),
      supplierColor: supplier?.color || avatarColorFromSeed(String(melhor.cotacao.idFornecedor)),
      subtotal: valor * quantidade,
      idCompraItem,
      valorUnitarioSel: valor,
      idFornecedorSel: melhor.cotacao.idFornecedor,
    });
  }

  return selecionados;
}

/**
 * Texto do modal de confirmação ao incluir ou remover produto (Solicitação e Cotação), ou `null`
 * quando não há o que avisar. Só avisa se algum convite gravado já foi enviado ou respondido: ao
 * salvar, o backend desativa o link enviado (se ainda ativo) e o próximo envio gera um link novo;
 * se o link já foi desativado por alteração anterior, o texto só orienta o reenvio; se ninguém
 * recebeu link (resposta só por planilha/matriz), o texto manda enviar, não reenviar. Remover
 * também apaga o item e os preços dele na cotação; incluir mantém todos os preços.
 * Produto removido sem idCompraItem nunca foi salvo: tirá-lo não muda nada no servidor.
 */
export function mensagemAlteracaoProdutoCotacao(
  acao: AcaoAlteracaoProdutoEnum,
  produto: CardProduct,
  cotacoes: CotacaoFornecedorStruct[],
  nomeFornecedor: (c: CotacaoFornecedorStruct) => string,
): string | null {
  if (acao === AcaoAlteracaoProdutoEnum.Remover && !produto.idCompraItem) return null;

  const alcancadas = cotacoes.filter(c => c.datetimeEnvio || c.datetimeResposta);
  if (!alcancadas.length) return null;

  // "A", "A e B", "A, B e C".
  const lista = (cs: CotacaoFornecedorStruct[]) => {
    const nomes = cs.map(nomeFornecedor);
    return nomes.length <= 1 ? (nomes[0] ?? '') : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
  };
  const aguardando = alcancadas.filter(c => !c.datetimeResposta);
  const respondidas = alcancadas.filter(c => c.datetimeResposta);
  const respondeu = respondidas.length > 1 ? 'responderam' : 'respondeu';
  // Resposta por planilha/matriz não passa pelo portal: só quem recebeu o envio tem link.
  // temLink = link ativo, que será desativado ao salvar; linkDesativado = já revogado por
  // alteração anterior e ainda não reenviado; temEnvio = alguém já recebeu algum link.
  const temLink = alcancadas.some(c => c.datetimeEnvio && !c.isLinkDesativado);
  const linkDesativado = !temLink && alcancadas.some(c => c.isLinkDesativado);
  const temEnvio = alcancadas.some(c => c.datetimeEnvio);

  const partes: string[] = [];
  if (aguardando.length && respondidas.length) {
    partes.push(`A cotação já foi enviada para ${lista(aguardando)}, e ${lista(respondidas)} já ${respondeu}.`);
  } else if (aguardando.length) {
    partes.push(`A cotação já foi enviada para ${lista(aguardando)}.`);
  } else {
    partes.push(`${lista(respondidas)} já ${respondeu} a cotação.`);
  }

  if (acao === AcaoAlteracaoProdutoEnum.Remover) {
    partes.push(temLink
      ? `Ao salvar, o link enviado será desativado e o produto “${produto.name}” sai da cotação.`
      : `Ao salvar, o produto “${produto.name}” sai da cotação.`);
    // Mesma regra de "ofertou" de calcularSelecaoCotacao.
    const comPreco = alcancadas.filter(c => (c.itens || [])
      .some(i => i.idCompraItem === produto.idCompraItem && !i.isNaoCotado && (i.valorUnitario || 0) > 0));
    if (comPreco.length) {
      partes.push(comPreco.length > 1
        ? `${lista(comPreco)} já informaram preço para ele. Esses preços serão perdidos.`
        : `${lista(comPreco)} já informou preço para ele. Esse preço será perdido.`);
    }
    if (temLink) partes.push('Será preciso reenviar a cotação para gerar um novo link.');
    else if (linkDesativado) partes.push('O link enviado já está desativado; reenvie a cotação para gerar um novo link.');
    return partes.join(' ');
  }

  if (temLink) partes.push('Ao salvar, o link enviado será desativado.');
  partes.push('Nenhum preço já informado será perdido.');
  if (linkDesativado) partes.push('O link enviado já está desativado; reenvie a cotação para gerar um novo link.');
  else if (temEnvio) partes.push('Depois de salvar, reenvie a cotação para gerar um novo link.');
  else partes.push('Depois de salvar, envie a cotação para gerar o link.');
  if (respondidas.length) {
    partes.push(temEnvio
      ? 'Para quem já respondeu, o reenvio reabre o link para completar a proposta, com os preços anteriores já preenchidos.'
      : 'Para quem já respondeu, o envio abre o link para completar a proposta, com os preços anteriores já preenchidos.');
  }
  return partes.join(' ');
}

// ===== PEDIDO =====
/**
 * Item do pedido. Campos monetários são NÚMEROS, como `PedidoState.frete`: os inputs usam
 * `[currencyMask]`, que só renderiza número — com string pt-BR apareciam vazios mesmo havendo
 * valor persistido. `null` = campo limpo (mask nullable).
 */
export interface PedidoItemView {
  idCompraItem?: number | null;
  sku: string;
  name: string;
  qty: number;
  unit: string;
  stock: number | null;
  preco: number | null;
  ipi: number | null;
  st: number | null;
  ibs: number | null;
  cbs: number | null;
  desconto: number | null;
}

export interface PedidoParcelaView {
  sequencia: number;
  dias: number | null;
  valor: number | null;
}

export const PEDIDO_FORMA_PAGAMENTO = ['Boleto bancário', 'PIX', 'Transferência (TED)', 'Cartão de crédito', 'Cheque', 'Dinheiro'];

/**
 * Envio do pedido ao fornecedor. Vive dentro do PedidoState (espelho compartilhado
 * shell ⇄ etapa): o filho dispara o pedido de envio, o shell salva + chama a API e
 * atualiza estes campos no MESMO objeto — o badge do filho reflete na hora.
 */
export interface PedidoEnvioView {
  idFornecedor: number | null;
  datetimeEnvio: string | null;
  datetimeConfirmacao: string | null;
  enviando: boolean;
}

/**
 * Estado editável do Pedido de Compra. O shell é o dono do objeto (usado pelo doSave,
 * que reenvia esses campos a partir de qualquer etapa); o componente da etapa de pedido
 * (ced-stage-pedido) edita o MESMO objeto por referência — espelho compartilhado,
 * robusto contra a destruição do filho ao trocar de etapa.
 */
export interface PedidoState {
  itens: PedidoItemView[];
  formaPagamentoOpcoes: string[];
  formaPagamento: string;
  prazoEntrega: string;
  frete: number | null;
  outrasDespesas: number | null;
  centroCusto: number | null;
  categoriaDre: number | null;
  observacoes: string;
  parcelas: PedidoParcelaView[];
  envio: PedidoEnvioView;
  /** Itens como o fornecedor os recebeu (retrato da etapa Pedido, no shell para sobreviver à troca de painel); some ao fechar o card. */
  itensRetratoEnvio?: { datetimeEnvio: string | null; itens: string[] };
}

/**
 * Monta o estado do pedido a partir do detalhe da compra.
 * Os itens carregam os tributos persistidos (compra_item). Casa por índice
 * com os produtos para herdar estoque/sku quando disponível.
 */
export function montarPedidoState(d: DetalheCompraResponse | undefined, products: CardProduct[]): PedidoState {
  const state: PedidoState = {
    itens: [],
    formaPagamentoOpcoes: [...PEDIDO_FORMA_PAGAMENTO],
    formaPagamento: '',
    prazoEntrega: '',
    frete: 0,
    outrasDespesas: 0,
    centroCusto: null,
    categoriaDre: null,
    observacoes: '',
    parcelas: [],
    envio: { idFornecedor: null, datetimeEnvio: null, datetimeConfirmacao: null, enviando: false },
  };

  const itens = d?.itens ?? [];
  state.itens = itens.map((i, idx) => {
    const p = products[idx];
    return {
      idCompraItem: i.idCompraItem,
      sku: i.codigoSku || p?.sku || '',
      name: i.nome,
      qty: i.quantidade,
      unit: i.unidade || p?.unit || 'un',
      stock: p?.stock ?? null,
      preco: i.valorUnitario ?? null,
      ipi: i.valorIpi ?? null,
      st: i.valorIcmsSt ?? null,
      ibs: i.valorIbs ?? null,
      cbs: i.valorCbs ?? null,
      desconto: i.valorDesconto ?? null,
    };
  });

  // Campos do pedido (compra).
  const c = d?.compra;
  if (c) {
    // Só aceita o que é forma de pagamento de fato. Valor fora da lista é resíduo de outra
    // coisa — tipicamente a expressão de parcelamento da cotação ("30", "30 60"), que vive nas
    // parcelas — e não pode virar opção do select; deixa vazio para o comprador escolher.
    if (c.condicaoPagamento && state.formaPagamentoOpcoes.includes(c.condicaoPagamento)) {
      state.formaPagamento = c.condicaoPagamento;
    }
    state.prazoEntrega = c.prazoEntrega || '';
    state.frete = c.valorFrete ?? 0;
    state.outrasDespesas = c.valorOutrasDespesas ?? 0;
    state.centroCusto = c.idCentroCusto ?? null;
    state.categoriaDre = c.idCategoriaFinanceira ?? null;
    state.observacoes = c.observacoesPedido || '';
    state.envio = {
      idFornecedor: c.idFornecedor ?? null,
      datetimeEnvio: c.datetimeEnvioPedido ?? null,
      datetimeConfirmacao: c.datetimeConfirmacaoPedido ?? null,
      enviando: false,
    };
  }

  // Parcelas (compra_parcela).
  state.parcelas = (d?.parcelas ?? [])
    .slice()
    .sort((a, b) => a.sequencia - b.sequencia)
    .map(p => ({ sequencia: p.sequencia, dias: p.dias ?? null, valor: p.valor ?? null }));

  return state;
}

/** Tributos informativos do item (IPI, ST, IBS, CBS) — não entram no subtotal. */
export function calcularTributosItemPedido(it: PedidoItemView): number {
  return (it.ipi ?? 0) + (it.st ?? 0) + (it.ibs ?? 0) + (it.cbs ?? 0);
}

/** Subtotal do item no pedido: quantidade × preço unitário − desconto. */
export function calcularSubtotalItemPedido(it: PedidoItemView): number {
  return it.qty * (it.preco ?? 0) - (it.desconto ?? 0);
}

/** Soma dos subtotais dos itens do pedido. */
export function calcularTotalItensPedido(state: PedidoState): number {
  return state.itens.reduce((soma, it) => soma + calcularSubtotalItemPedido(it), 0);
}

/** Total do pedido: itens + frete + outras despesas. Espelha compra.valorTotal do backend. */
export function calcularTotalPedido(state: PedidoState): number {
  return calcularTotalItensPedido(state) + parseBR(state.frete) + parseBR(state.outrasDespesas);
}
